import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '@/utils/api';

export function useEvents(filters = {}) {
  return useQuery({
    queryKey: ['events', filters],
    queryFn: async () => {
      const response = await api.get('/events', { params: filters });
      return response.data;
    }
  });
}

export function useUpcomingEvents(days = 7) {
  return useQuery({
    queryKey: ['events', 'upcoming', days],
    queryFn: async () => {
      const response = await api.get('/events/upcoming', { params: { days } });
      return response.data;
    }
  });
}

export function useEventStats() {
  return useQuery({
    queryKey: ['events', 'stats'],
    queryFn: async () => {
      const response = await api.get('/events/stats');
      return response.data;
    }
  });
}

export function useEvent(eventId) {
  return useQuery({
    queryKey: ['events', eventId],
    queryFn: async () => {
      const response = await api.get(`/events/${eventId}`);
      return response.data;
    },
    enabled: !!eventId
  });
}

export function useEventsForEmail(emailId) {
  return useQuery({
    queryKey: ['events', 'email', emailId],
    queryFn: async () => {
      const response = await api.get(`/emails/${emailId}/events`);
      return response.data;
    },
    enabled: !!emailId
  });
}

export function useCreateEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (eventData) => {
      const response = await api.post('/events', eventData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
    }
  });
}

export function useUpdateEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ eventId, updates }) => {
      const response = await api.put(`/events/${eventId}`, updates);
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['events', variables.eventId] });
      queryClient.invalidateQueries({ queryKey: ['events'] });
    }
  });
}

export function useDeleteEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (eventId) => {
      await api.delete(`/events/${eventId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
    }
  });
}

export function useCompleteEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (eventId) => {
      const response = await api.post(`/events/${eventId}/complete`);
      return response.data;
    },
    onSuccess: (_, eventId) => {
      queryClient.invalidateQueries({ queryKey: ['events', eventId] });
      queryClient.invalidateQueries({ queryKey: ['events'] });
    }
  });
}

export function useCancelEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (eventId) => {
      const response = await api.post(`/events/${eventId}/cancel`);
      return response.data;
    },
    onSuccess: (_, eventId) => {
      queryClient.invalidateQueries({ queryKey: ['events', eventId] });
      queryClient.invalidateQueries({ queryKey: ['events'] });
    }
  });
}

export function useLinkEmailToEvent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ eventId, emailId, relevance }) => {
      await api.post(`/events/${eventId}/link`, { email_id: emailId, relevance });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['events', variables.eventId] });
      queryClient.invalidateQueries({ queryKey: ['events', 'email', variables.emailId] });
    }
  });
}
